import { io, Socket } from 'socket.io-client';
import './styles.css';
import {
  type ArcadePlayer,
  type MoneyDelta,
  getArcadePlayer,
  ensureArcadePlayer,
  getBalance,
  loadBankAccount,
  calcAndApplyRoundMoney,
  clampBetAmount,
  fmtMoney,
  fmtDelta,
  STARTING_BALANCE,
  DEFAULT_BET_AMOUNT,
  MIN_BET_AMOUNT,
  MAX_BET_AMOUNT,
} from './session-bridge';

/* ══════════════════════════════════════════
   TYPES
══════════════════════════════════════════ */
type Suit = 'S' | 'C' | 'D' | 'H';
type Rank = '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A' | '2';

interface Card { id: string; rank: Rank; suit: Suit; }

interface RoomSummary {
  id: string;
  name: string;
  hostId: string;
  maxPlayers: number;
  betAmount: number;
  playerCount: number;
  players: Array<{ socketId: string; name: string; seat: number }>;
  status: 'WAITING' | 'PLAYING' | 'FINISHED';
  createdAt: number;
  roundNumber: number;
}

interface PlayerView {
  socketId: string;
  name: string;
  seat: number;
  cardCount: number;
  hasPlayed: boolean;
  passed: boolean;
  totalScore: number;
  hand?: Card[];
}

interface ComboView {
  type: string;
  cards: Card[];
  highRankValue?: number;
  highSuitValue?: number;
  length?: number;
  pairCount?: number;
}
interface SuggestedPlayView { combo: ComboView; cardIds: string[]; }

interface ScoreLine {
  playerId: string;       // socket.id of that player
  playerName: string;
  remainingCards: number;
  basePenalty: number;
  congPenalty: number;
  rottenPenalty: number;
  totalPenalty: number;
  rottenItems: string[];
}

interface RoomSnapshot {
  room: {
    id: string;
    name: string;
    hostId: string;
    maxPlayers: number;
    betAmount: number;
    players: PlayerView[];
    status: 'WAITING' | 'PLAYING' | 'FINISHED';
    roundNumber: number;
    firstGame: boolean;
    currentTurnId?: string;
    turnStartedAt?: number;
    turnDeadlineAt?: number;
    openerId?: string;
    openingRequiredCardId?: string;
    lastWinnerId?: string;
    lastWinnerName?: string;
    scoreLines?: ScoreLine[];
    instantWin?: { playerId: string; playerName: string; name: string };
    lastPlay?: {
      playerId: string;
      playerName: string;
      combo: ComboView;
      playedAt: number;
    };
    clearedPlay?: {
      playerId: string;
      playerName: string;
      combo: ComboView;
      playedAt: number;
    };
    log: string[];
  };
  serverNow?: number;
  me?: {
    socketId: string;
    name: string;
    seat: number;
    hand: Card[];
    isTurn: boolean;
    suggestedPlays?: SuggestedPlayView[];
  };
}

/* ══════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════ */
const SUIT_SYMBOLS: Record<Suit, string> = { S: '♠', C: '♣', D: '♦', H: '♥' };
const RANKS: Rank[] = ['3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A', '2'];
const SUITS: Suit[] = ['S', 'C', 'D', 'H'];
const COMBO_NAMES: Record<string, string> = {
  single: 'Lẻ',
  pair: 'Đôi',
  triple: 'Sám cô',
  fourKind: 'Tứ quý',
  straight: 'Sảnh',
  doubleSequence: 'Đôi thông',
};

/* ══════════════════════════════════════════
   BACK NAVIGATION
   Handles three scenarios:
   1. Served as file:// (production static) — navigate to parent
   2. Vite dev server on localhost — dashboard is a separate file, use absolute file path
   3. Deployed on same origin — relative ../index.html works
══════════════════════════════════════════ */
function navigateToDashboard(): void {
  const { href } = window.location;

  // If served as file:// — strip two path segments to get to frontend/index.html
  if (href.startsWith('file://')) {
    // e.g. file:///Users/.../frontend/tien-len-mien-nam/index.html
    //   → file:///Users/.../frontend/index.html
    const parent = href.split('/').slice(0, -2).join('/') + '/index.html';
    window.location.href = parent;
    return;
  }

  // On Vite dev server: the dashboard is a static file at the filesystem level.
  // Best UX: go back in history if there's a previous page, otherwise open dashboard.
  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  // Fallback: try to derive the file:// URL from known project structure
  // (only works on local dev where user opened dashboard as a file)
  const fileUrl = 'file:///Users/haidinh/Desktop/game-ssg/frontend/index.html';
  window.location.href = fileUrl;
}


/* ══════════════════════════════════════════
   SOCKET
══════════════════════════════════════════ */
const params = new URLSearchParams(window.location.search);
const defaultServer = 'https://caro.101conceptvn.com';
const serverUrl = params.get('server') || defaultServer;
const socket: Socket = io(serverUrl, { transports: ['websocket', 'polling'] });

/* ══════════════════════════════════════════
   STATE
══════════════════════════════════════════ */
const state: {
  connected: boolean;
  rooms: RoomSummary[];
  snapshot: RoomSnapshot | null;
  selected: Set<string>;
  playerName: string;
  roomName: string;
  betAmount: number;
  error: string;
  // Session + money
  arcadePlayer: ArcadePlayer | null;
  balance: number;
  lastMoneyDelta: MoneyDelta | null;
  lastProcessedFinishKey: string;  // prevent double-charging
  serverClockOffsetMs: number;
} = {
  connected: false,
  rooms: [],
  snapshot: null,
  selected: new Set(),
  playerName: localStorage.getItem('tienlen_player_name') || '',
  roomName: '',
  betAmount: Number(localStorage.getItem('tienlen_bet_amount')) || DEFAULT_BET_AMOUNT,
  error: '',
  arcadePlayer: null,
  balance: STARTING_BALANCE,
  lastMoneyDelta: null,
  lastProcessedFinishKey: '',
  serverClockOffsetMs: 0,
};

const app = document.querySelector<HTMLDivElement>('#app')!;
if (!app) throw new Error('Missing #app');

/* ══════════════════════════════════════════
   SESSION INIT — runs on page load
══════════════════════════════════════════ */
async function initSession(): Promise<void> {
  // Try existing stored player first (no dialog)
  let player = getArcadePlayer();
  if (!player) {
    // No stored player → prompt for name (pass stored tienlen name as default)
    player = await ensureArcadePlayer(state.playerName || undefined);
  }
  if (player) {
    state.arcadePlayer = player;
    state.playerName = player.name;
    state.balance = await loadBankAccount(player);
    localStorage.setItem('tienlen_player_name', player.name);
  }
  render();
}

/* ══════════════════════════════════════════
   SOCKET EVENTS
══════════════════════════════════════════ */
socket.on('connect', () => {
  state.connected = true;
  socket.emit('tienlen-get-rooms');
  render();
});

socket.on('disconnect', () => {
  state.connected = false;
  render();
});

socket.on('tienlen-room-list', (data: { rooms: RoomSummary[] }) => {
  state.rooms = data.rooms;
  render();
});

socket.on('tienlen-room-created', (data: { room: RoomSnapshot }) => {
  state.snapshot = data.room;
  state.selected.clear();
  state.lastMoneyDelta = null;
  state.lastProcessedFinishKey = '';
  render();
});

socket.on('tienlen-room-joined', (data: { room: RoomSnapshot }) => {
  state.snapshot = data.room;
  state.selected.clear();
  state.lastMoneyDelta = null;
  state.lastProcessedFinishKey = '';
  render();
});

socket.on('tienlen-state', (snapshot: RoomSnapshot) => {
  if (snapshot.serverNow) {
    state.serverClockOffsetMs = snapshot.serverNow - Date.now();
  }

  const prevStatus = state.snapshot?.room.status;
  const prevRound  = state.snapshot?.room.roundNumber;
  const isNewDeal  = snapshot.room.status === 'PLAYING' &&
    (prevStatus !== 'PLAYING' || snapshot.room.roundNumber !== prevRound);

  // ── Money: charge/reward when a round finishes ──
  const finishKey = `${snapshot.room.id}:${snapshot.room.roundNumber}`;
  const isFinish = snapshot.room.status === 'FINISHED' &&
    finishKey !== state.lastProcessedFinishKey;

  if (isFinish && state.arcadePlayer && snapshot.room.scoreLines?.length) {
    state.lastProcessedFinishKey = finishKey;
    const myLine = snapshot.room.scoreLines.find(
      l => l.playerId === snapshot.me?.socketId
    );
    const isWinner = snapshot.room.lastWinnerId === snapshot.me?.socketId;
    const isInstantWin = !!snapshot.room.instantWin &&
      snapshot.room.instantWin.playerId === snapshot.me?.socketId;

    const result = calcAndApplyRoundMoney({
      playerId: state.arcadePlayer.playerId,
      betAmount: getRoomBetAmount(snapshot.room),
      isWinner,
      isInstantWin,
      scoreLines: snapshot.room.scoreLines,
      congPenalty:   myLine?.congPenalty   ?? 0,
      rottenPenalty: myLine?.rottenPenalty ?? 0,
      rottenItems:   myLine?.rottenItems   ?? [],
    });
    state.balance = result.newBalance;
    state.lastMoneyDelta = result;
  }

  // Clear selection cards that are no longer in hand
  state.snapshot = snapshot;
  for (const id of Array.from(state.selected)) {
    if (!snapshot.me?.hand.some(c => c.id === id)) state.selected.delete(id);
  }

  // Reset money delta display when new deal starts
  if (isNewDeal) state.lastMoneyDelta = null;

  render();
  updateTurnCountdown();

  // Shuffle animation on new deal
  if (isNewDeal) {
    const deck = document.querySelector('.deck-stack');
    if (deck) {
      deck.classList.remove('is-dealing');
      void (deck as HTMLElement).offsetWidth;
      deck.classList.add('is-dealing');
      setTimeout(() => deck.classList.remove('is-dealing'), 700);
    }
  }
});

socket.on('tienlen-left-room', () => {
  state.snapshot = null;
  state.selected.clear();
  state.lastMoneyDelta = null;
  state.lastProcessedFinishKey = '';
  socket.emit('tienlen-get-rooms');
  render();
});

socket.on('tienlen-error', (data: { message: string }) => {
  state.error = data.message;
  render();
  setTimeout(() => {
    if (state.error === data.message) { state.error = ''; render(); }
  }, 4000);
});

/* ══════════════════════════════════════════
   RENDER ROOT
══════════════════════════════════════════ */
function render(): void {
  app.innerHTML = `
    <div class="shell">
      ${renderTopbar()}
      ${state.error ? `<div class="toast">${esc(state.error)}</div>` : ''}
      ${state.snapshot ? renderTable(state.snapshot) : renderLobby()}
    </div>
  `;
  bindEvents();
}

/**
 * Lightweight update of action-bar only — no full re-render.
 * Preserves card elements and their CSS transitions.
 */
function updateActionBar(): void {
  const me   = state.snapshot?.me;
  const room = state.snapshot?.room;
  if (!me || !room) return;

  const selectedCards = me.hand.filter(c => state.selected.has(c.id));
  const canPlay = room.status === 'PLAYING' && me.isTurn && state.selected.size > 0;
  const canPass = room.status === 'PLAYING' && me.isTurn && !!room.lastPlay;
  const suggestedPlay = getSelectedSuggestion(me);

  const infoEl = q<HTMLDivElement>('.selected-info');
  if (infoEl) {
    infoEl.textContent = selectedCards.length
      ? `Đã chọn: ${selectedCards.map(cardText).join(' ')}`
      : me.isTurn
        ? suggestedPlay
          ? `Gợi ý: ${comboLabel(suggestedPlay.combo)}`
          : 'Không có bài chặn, hãy bỏ lượt'
        : 'Chờ lượt của bạn…';
    infoEl.classList.toggle('has-selection', selectedCards.length > 0);
  }

  const playBtn = q<HTMLButtonElement>('#playCards');
  if (playBtn) playBtn.disabled = !canPlay;

  const passBtn = q<HTMLButtonElement>('#passTurn');
  if (passBtn) passBtn.disabled = !canPass;

  const hintBtn = q<HTMLButtonElement>('#hintPlay');
  if (hintBtn) hintBtn.disabled = !(room.status === 'PLAYING' && me.isTurn && !!getSelectedSuggestion(me));
}

/* ══════════════════════════════════════════
   TOPBAR
══════════════════════════════════════════ */
function renderTopbar(): string {
  const accountChip = state.arcadePlayer
    ? `<div class="account-chip" title="${escAttr(state.arcadePlayer.name)} · ${fmtMoney(state.balance)}">
        <span class="player-avatar">${esc(state.arcadePlayer.name.slice(0,1).toUpperCase())}</span>
        <span class="account-chip-text">
          <span class="account-chip-name">${esc(state.arcadePlayer.name)}</span>
          <span class="account-chip-balance">${fmtMoney(state.balance)}</span>
        </span>
       </div>`
    : '';

  return `
    <header class="topbar">
      <button class="back-link" id="backBtn" aria-label="Về dashboard" title="Về dashboard">←</button>
      <div class="topbar-title">
        <span class="eyebrow">♠ Realtime · Tiến lên miền Nam</span>
        <h1>Tú Lơ Khơ · Southern Hold'em</h1>
      </div>
      <div class="topbar-right">
        ${accountChip}
        <div class="connection ${state.connected ? 'is-online' : ''}">
          <span class="dot"></span>
          <span class="conn-label">${state.connected ? 'Online' : '…'}</span>
        </div>
      </div>
    </header>
  `;
}

/* ══════════════════════════════════════════
   LOBBY
══════════════════════════════════════════ */
function renderLobby(): string {
  const selectedBet = clampBetAmount(state.betAmount);
  const rooms = state.rooms.map(room => {
    const roomBetAmount = getRoomBetAmount(room);
    const notEnoughBank = !!state.arcadePlayer && state.balance < roomBetAmount;
    const joinDisabled = room.status !== 'WAITING' || notEnoughBank;
    const joinLabel = notEnoughBank ? 'Không đủ tiền' : 'Vào bàn →';
    return `
      <article class="room-card">
        <div>
          <p class="room-status">${room.status === 'WAITING' ? '🟢 Đang chờ' : room.status === 'PLAYING' ? '🔴 Đang đánh' : '⚫ Kết thúc'}</p>
          <h3>${esc(room.name)}</h3>
          <p>${room.playerCount}/${room.maxPlayers} người · Ván ${room.roundNumber || 0}</p>
          <p class="room-bet">Cược ${fmtMoney(roomBetAmount)}</p>
        </div>
        <div class="room-players">
          ${room.players.map(p => `<span>${esc(p.name)}</span>`).join('')}
        </div>
        <button class="btn btn-secondary btn-sm join-room" data-room-id="${room.id}"
          ${joinDisabled ? 'disabled' : ''}>
          ${joinLabel}
        </button>
      </article>
    `;
  }).join('');

  const greeting = state.arcadePlayer
    ? `Xin chào, <strong>${esc(state.arcadePlayer.name)}</strong>! Bank account: <strong class="gold">${fmtMoney(state.balance)}</strong>`
    : 'Nhập tên để bắt đầu chơi';

  return `
    <main class="lobby">
      <!-- Intro panel -->
      <section class="intro-panel">
        <div class="card-fan" aria-hidden="true">
          <div class="mini-card red">2<br>♥</div>
          <div class="mini-card black">K<br>♠</div>
          <div class="mini-card red">A<br>♦</div>
          <div class="mini-card black">3<br>♣</div>
        </div>
        <div class="intro-text">
          <p class="eyebrow" style="margin-bottom:6px">🎴 Không tiền thật · Giải trí</p>
          <h2>Đánh hết bài trước · Về Nhất!</h2>
          <p class="greeting-line">${greeting}</p>
          <p>Tiến lên miền Nam chuẩn luật: 3♠ nhỏ nhất, 2♥ lớn nhất. Hỗ trợ chặt heo, ba đôi thông, tứ quý, tới trắng, cóng và thối hàng.</p>
        </div>
      </section>

      <!-- Create panel -->
      <aside class="create-panel">
        <h2>🎮 Tạo bàn mới</h2>
        <div class="form-field">
          <label for="playerName">Tên của bạn</label>
          <input id="playerName" maxlength="24" value="${escAttr(state.playerName)}"
            placeholder="Nhập tên hiển thị…"
            ${state.arcadePlayer ? 'readonly title="Tên lấy từ tài khoản. Đổi tên tại Dashboard."' : ''} />
        </div>
        <div class="form-field">
          <label for="roomName">Tên bàn</label>
          <input id="roomName" maxlength="60" value="${escAttr(state.roomName)}" placeholder="Ví dụ: Bàn chiều thứ 6" />
        </div>
        <div class="form-field">
          <label for="betAmount">Mức cược mỗi ván</label>
          <input id="betAmount" type="number" min="${MIN_BET_AMOUNT}" max="${MAX_BET_AMOUNT}" step="1000"
            value="${selectedBet}" />
          <small class="field-hint" id="betAmountHint">${fmtMoney(selectedBet)}</small>
        </div>
        ${!state.arcadePlayer ? `
          <p class="session-hint">💡 Đăng nhập để lưu số dư và xuất hiện trên bảng xếp hạng</p>
          <button class="btn btn-ghost btn-sm" id="loginBtn">Đăng nhập</button>
        ` : ''}
        <button class="btn btn-primary" id="createRoom" style="margin-top:auto">
          ✚ Tạo bàn
        </button>
      </aside>

      <!-- Rooms panel -->
      <section class="rooms-panel">
        <div class="rooms-header">
          <div>
            <h2>Bàn đang mở</h2>
            <p>2–4 người · 13 lá/người</p>
          </div>
          <button class="icon-btn" id="refreshRooms" aria-label="Làm mới danh sách">↻</button>
        </div>
        <div class="room-grid">
          ${rooms || '<div class="empty-state">Chưa có bàn nào. Hãy tạo bàn mới để bắt đầu! 🎴</div>'}
        </div>
      </section>
    </main>
  `;
}

/* ══════════════════════════════════════════
   TABLE
══════════════════════════════════════════ */
function renderTable(snapshot: RoomSnapshot): string {
  const room      = snapshot.room;
  const me        = snapshot.me;
  const isHost    = room.hostId === socket.id;
  const isPlaying = room.status === 'PLAYING';
  const isFinished = room.status === 'FINISHED';
  const roomBetAmount = getRoomBetAmount(room);
  const hasEnoughBankForBet = !state.arcadePlayer || state.balance >= roomBetAmount;
  // Host can start if: waiting with 2+ players, OR after a finished round
  const canStart  = isHost && !isPlaying && room.players.length >= 2 && hasEnoughBankForBet;
  const canPlay   = isPlaying && me?.isTurn && state.selected.size > 0;
  const canPass   = isPlaying && me?.isTurn && !!room.lastPlay;
  const suggestedPlay = me ? getSelectedSuggestion(me) : null;
  const canHint = isPlaying && !!me?.isTurn && !!suggestedPlay;
  const suggestedCardIds = new Set(suggestedPlay?.cardIds || []);

  const selectedCards = me?.hand.filter(c => state.selected.has(c.id)) || [];
  const selectedText  = selectedCards.length
    ? `Đã chọn: ${selectedCards.map(cardText).join(' ')}`
    : me?.isTurn
      ? suggestedPlay
        ? `Gợi ý: ${comboLabel(suggestedPlay.combo)}`
        : 'Không có bài chặn, hãy bỏ lượt'
      : 'Chờ lượt của bạn…';

  const currentPlayer = room.players.find(p => p.socketId === room.currentTurnId);

  return `
    <div class="table-layout">
      <!-- ══ MAIN TABLE ══ -->
      <div class="table-main">
        <div class="table-surface">
          <!-- Header -->
          <div class="table-header">
            <div class="table-room-badge">
              <div>Phòng: <strong>${esc(room.id.replace(/^tl_/, '').slice(-6))}</strong></div>
              <div>Cược: <strong>${fmtMoney(roomBetAmount)}</strong></div>
            </div>
            <div class="table-status-pill">${renderStatusText(room, currentPlayer)}</div>
            ${isPlaying ? renderTurnClock(room) : ''}
            <div class="table-top-actions">
              ${canStart && !isFinished ? `<button class="btn btn-primary btn-sm start-game">▶ Bắt đầu</button>` : ''}
              <button class="table-exit" id="leaveRoom" aria-label="Rời bàn">↪</button>
            </div>
          </div>

          <!-- Arena (overflow visible so winner banner isn't clipped) -->
          <div class="table-arena">
            <div class="round-table">
              <div class="table-ring"></div>
              <div class="deck-stack" aria-hidden="true">
                <span></span><span></span><span></span>
              </div>
              <div class="center-play">
                ${renderCenterPlay(room)}
              </div>
            </div>
            ${room.players.map((p, i) => renderSeat(p, room, i, room.players.length)).join('')}
          </div>

          <!-- Winner overlay — OUTSIDE round-table so no overflow clip -->
          ${isFinished ? renderWinnerBanner(room, canStart, isHost) : ''}

          <!-- Action bar -->
          <div class="action-bar">
            <div class="selected-info ${selectedCards.length ? 'has-selection' : ''}">${selectedText}</div>
            <div class="actions">
              <button class="table-action table-action--red" id="passTurn" ${canPass ? '' : 'disabled'}>Bỏ lượt</button>
              <button class="table-action table-action--silver" id="hintPlay" ${canHint ? '' : 'disabled'}>Gợi ý</button>
              <button class="table-action table-action--green" id="playCards" ${canPlay ? '' : 'disabled'}>Đánh bài</button>
            </div>
          </div>

          <!-- Hand -->
          <div class="hand-wrap">
            ${(me?.hand || []).map((card, idx, arr) =>
              renderCard(card, state.selected.has(card.id), false, idx, arr.length, suggestedCardIds.has(card.id))
            ).join('')}
          </div>
        </div>
      </div>

      <!-- ══ SIDE PANEL ══ -->
      <aside class="side-panel">
        ${renderSideScore(room)}
        ${renderSidePlayers(room)}
        ${renderSideRules()}
        ${renderSideLog(room)}
      </aside>
    </div>
  `;
}

/* ── Winner overlay (absolute over table-surface, not inside round-table) ── */
function renderWinnerBanner(room: RoomSnapshot['room'], canStart: boolean, isHost: boolean): string {
  const moneyLines = state.lastMoneyDelta?.lines ?? [];
  const delta = state.lastMoneyDelta?.delta ?? 0;
  const deltaClass = delta >= 0 ? 'delta-pos' : 'delta-neg';

  return `
    <div class="winner-banner">
      <div class="crown">👑</div>
      <h2>${esc(room.lastWinnerName || 'Người chơi')} về Nhất!</h2>
      ${room.instantWin ? `<p class="instant-tag">⭐ Tới trắng: ${esc(room.instantWin.name)}</p>` : ''}
      ${state.lastMoneyDelta ? `
        <div class="money-result">
          <span class="money-delta ${deltaClass}">${fmtDelta(delta)}</span>
          <span class="money-balance">→ ${fmtMoney(state.lastMoneyDelta.newBalance)}</span>
        </div>
        <div class="money-lines">
          ${moneyLines.map(l => `<span>${esc(l)}</span>`).join('')}
        </div>
      ` : ''}
      <div class="winner-actions">
        ${canStart
          ? `<button class="btn btn-primary start-game">♻ Chia ván mới</button>`
          : isHost
            ? `<p class="banner-hint">Bank account không đủ để bắt đầu ván mới.</p>`
            : `<p class="banner-hint">Chờ chủ phòng chia ván mới…</p>`
        }
      </div>
    </div>
  `;
}

/* ── Center play area ── */
function renderCenterPlay(room: RoomSnapshot['room']): string {
  if (room.lastPlay) {
    return `
      <span class="player-name">${esc(room.lastPlay.playerName)}</span>
      <span class="combo-label">${comboLabel(room.lastPlay.combo)}</span>
      <div class="played-cards">
        ${room.lastPlay.combo.cards.map(c => renderCard(c, false, true)).join('')}
      </div>
    `;
  }
  if (room.clearedPlay) {
    return `
      <span class="round-note">Vòng trước</span>
      <span class="player-name">${esc(room.clearedPlay.playerName)} giữ lượt</span>
      <span class="combo-label">${comboLabel(room.clearedPlay.combo)}</span>
      <div class="played-cards played-cards-history">
        ${room.clearedPlay.combo.cards.map(c => renderCard(c, false, true)).join('')}
      </div>
    `;
  }
  return `<p>${renderCenterPrompt(room)}</p>`;
}

/* ── Seat around the table ── */
function renderSeat(
  player: PlayerView,
  room: RoomSnapshot['room'],
  index: number,
  total: number
): string {
  const isCurrent = room.currentTurnId === player.socketId;
  const isMe      = player.socketId === socket.id;
  const isWinner  = room.lastWinnerId === player.socketId;
  const angle     = -90 + index * (360 / Math.max(total, 1));

  const flags: string[] = [];
  if (player.passed)  flags.push('<span>Bỏ lượt</span>');
  if (isWinner)       flags.push('<span>👑 Nhất</span>');
  if (!player.hasPlayed && room.status === 'PLAYING') flags.push('<span>Chưa đánh</span>');

  return `
    <article class="seat ${isCurrent ? 'is-turn' : ''} ${isMe ? 'is-me' : ''}" style="--angle:${angle}deg">
      <div class="avatar">${esc(player.name.slice(0, 1).toUpperCase())}</div>
      <div class="seat-name">
        ${esc(player.name)}
        ${isMe ? '<span class="me-tag">bạn</span>' : ''}
      </div>
      <div class="seat-score">${player.totalScore > 0 ? '+' : ''}${player.totalScore}</div>
      <div class="seat-sub">${player.cardCount} lá</div>
      ${flags.length ? `<div class="seat-flags">${flags.join('')}</div>` : ''}
    </article>
  `;
}

/* ══════════════════════════════════════════
   SIDE PANELS
══════════════════════════════════════════ */
function renderSideScore(room: RoomSnapshot['room']): string {
  if (room.status === 'FINISHED' && room.scoreLines?.length) {
    const lines = room.scoreLines.map(l => {
      const isWinner = room.lastWinnerId === l.playerId;
      return `
        <div class="score-line ${isWinner ? 'score-line--winner' : ''}">
          <strong>${esc(l.playerName)}</strong>
          <span class="${isWinner ? 'winner-text' : 'penalty'}">${isWinner ? '🥇 Nhất' : (l.totalPenalty > 0 ? `-${l.totalPenalty}` : '0')}</span>
          <small>${l.remainingCards} lá còn lại${l.congPenalty > 0 ? ' · cóng' : ''}${l.rottenPenalty > 0 ? ` · thối` : ''}</small>
          ${l.rottenItems.length ? `<small class="rotten-items">${l.rottenItems.map(esc).join(', ')}</small>` : ''}
        </div>
      `;
    }).join('');

    // My money result
    const myMoneyHtml = state.lastMoneyDelta ? `
      <div class="my-money-result">
        <div class="my-money-label">💰 Bank account của bạn</div>
        <div class="my-money-row">
          <span class="${state.lastMoneyDelta.delta >= 0 ? 'delta-pos' : 'delta-neg'}">${fmtDelta(state.lastMoneyDelta.delta)}</span>
          <span class="arrow">→</span>
          <strong>${fmtMoney(state.lastMoneyDelta.newBalance)}</strong>
        </div>
        ${state.lastMoneyDelta.lines.map(l => `<small>${esc(l)}</small>`).join('')}
      </div>
    ` : '';

    return `
      <div class="side-section score-panel">
        <div class="side-section-head">🏆 Kết quả ván ${room.roundNumber}</div>
        <div class="side-section-body">
          <div class="balance-display">
            <span class="balance-display-label">Mức cược</span>
            <span class="balance-display-amount">${fmtMoney(getRoomBetAmount(room))}</span>
          </div>
          ${room.instantWin ? `<div class="instant-win">⭐ Tới trắng: ${esc(room.instantWin.name)}</div>` : ''}
          <div class="score-lines">${lines}</div>
          ${myMoneyHtml}
          ${room.hostId === socket.id
            ? `<button class="btn btn-primary start-game" style="width:100%;margin-top:12px">♻ Chia ván mới</button>`
            : `<p class="score-empty" style="margin-top:10px">Chờ chủ phòng chia ván mới…</p>`
          }
        </div>
      </div>
    `;
  }

  return `
    <div class="side-section score-panel">
      <div class="side-section-head">📊 Bảng điểm</div>
      <div class="side-section-body">
        ${state.arcadePlayer
          ? `<div class="balance-display">
               <span class="balance-display-label">Bank account</span>
               <span class="balance-display-amount">${fmtMoney(state.balance)}</span>
             </div>`
          : ''
        }
        <div class="balance-display">
          <span class="balance-display-label">Mức cược</span>
          <span class="balance-display-amount">${fmtMoney(getRoomBetAmount(room))}</span>
        </div>
        <p class="score-empty">Điểm sẽ hiển thị khi ván kết thúc. Ai về Nhất sẽ không bị trừ điểm.</p>
      </div>
    </div>
  `;
}

function renderSidePlayers(room: RoomSnapshot['room']): string {
  const rows = room.players.map(p => {
    const isMe   = p.socketId === socket.id;
    const isHost = room.hostId === p.socketId;
    return `
      <div class="player-row">
        <div class="av-sm">${esc(p.name.slice(0, 1).toUpperCase())}</div>
        <span class="player-row-name">${esc(p.name)}${isMe ? ' <em>(bạn)</em>' : ''}</span>
        ${isHost ? '<span class="host-tag">Host</span>' : ''}
        <span class="card-count">${p.cardCount} lá</span>
      </div>
    `;
  }).join('');

  return `
    <div class="side-section" style="flex-shrink:0">
      <div class="side-section-head">👥 Người chơi (${room.players.length}/${room.maxPlayers})</div>
      <div class="side-section-body">
        <div class="players-waiting">${rows}</div>
      </div>
    </div>
  `;
}

function renderSideRules(): string {
  const rules = [
    ['♠', 'Thứ tự: 3 &lt; 4 &lt; … &lt; K &lt; A &lt; 2. Chất: ♠ &lt; ♣ &lt; ♦ &lt; ♥'],
    ['🃏', 'Đánh đúng loại (lẻ/đôi/sảnh…), cùng số lá, mạnh hơn bộ trước'],
    ['🚫', 'Đã bỏ lượt thì bị khóa cho đến hết vòng đó'],
    ['⚔', 'Ba đôi thông chặt 1 heo. Tứ quý chặt 1 heo hoặc 3 đôi thông'],
    ['💎', 'Bốn đôi thông chặt đôi heo, tứ quý và ba đôi thông'],
    ['⭐', 'Tới trắng: tứ quý 2, rồng (3→2 cùng chất), hoặc 13 đen/đỏ'],
    ['🏅', 'Cóng: còn 12 lá khi người khác hết. Thối: còn heo/đôi lớn trên tay'],
    ['💰', 'Tiền: Người thắng thu đúng tổng tiền người thua bị trừ. Thua -1x, Cóng -2x, Thối -1x/món'],
  ];
  const rows = rules.map(([icon, text]) => `
    <div class="rule-row">
      <span class="rule-icon">${icon}</span>
      <span>${text}</span>
    </div>
  `).join('');
  return `
    <div class="side-section" style="flex-shrink:0">
      <div class="side-section-head">📖 Luật chơi</div>
      <div class="side-section-body">
        <div class="rules-text">${rows}</div>
      </div>
    </div>
  `;
}

function renderSideLog(room: RoomSnapshot['room']): string {
  const lines = room.log.slice(0, 12).map(line =>
    `<div class="log-line">${esc(line)}</div>`
  ).join('');
  return `
    <div class="side-section log-panel">
      <div class="side-section-head">📋 Diễn biến</div>
      <div class="side-section-body">
        <div class="log-lines">${lines || '<div class="log-line" style="color:var(--subtle)">Chưa có diễn biến.</div>'}</div>
      </div>
    </div>
  `;
}

/* ══════════════════════════════════════════
   CARD RENDER
══════════════════════════════════════════ */
function renderCard(card: Card, selected = false, compact = false, idx = 0, total = 1, suggested = false): string {
  const red = card.suit === 'D' || card.suit === 'H';
  let styleAttr = '';
  if (!compact) {
    const spread   = Math.min(total * 2.8, 26);
    const fanAngle = total > 1 ? ((idx / (total - 1)) - 0.5) * spread : 0;
    const fanY     = total > 1 ? Math.abs((idx / (total - 1)) - 0.5) * Math.min(total * 1.8, 14) : 0;
    const dealRot  = (idx % 2 === 0 ? -1 : 1) * (4 + (idx % 3) * 2);
    styleAttr = `style="
      --card-fan-base: rotate(${fanAngle}deg) translateY(${fanY}px);
      --deal-rotate: ${dealRot}deg;
      z-index: ${selected ? 30 : idx};
      animation-delay: ${idx * 0.045}s;
    "`;
  }

  return `
    <button class="card ${red ? 'red' : 'black'} ${selected ? 'is-selected' : ''} ${compact ? 'is-compact' : ''}"
      ${suggested && !selected && !compact ? 'data-suggested="true"' : ''}
      data-card-id="${card.id}" aria-label="${cardText(card)}" ${styleAttr}>
      <span>${card.rank}</span>
      <strong>${SUIT_SYMBOLS[card.suit]}</strong>
    </button>
  `;
}

/* ══════════════════════════════════════════
   HELPERS
══════════════════════════════════════════ */
function cardText(card: Card): string {
  return `${card.rank}${SUIT_SYMBOLS[card.suit]}`;
}

function cardTextFromId(cardId: string): string {
  const suit = cardId.slice(-1) as Suit;
  const rank  = cardId.slice(0, -1);
  return `${rank}${SUIT_SYMBOLS[suit] || ''}`;
}

function comboLabel(combo: ComboView): string {
  const base = combo.type === 'doubleSequence'
    ? `${combo.pairCount || 0} đôi thông`
    : COMBO_NAMES[combo.type] || combo.type;
  return `${base}: ${combo.cards.map(cardText).join(' ')}`;
}

function renderTurnClock(room: RoomSnapshot['room']): string {
  const remainingSeconds = getTurnRemainingSeconds(room);
  const pct = getTurnRemainingPct(room);
  return `
    <div class="turn-clock ${remainingSeconds <= 5 ? 'is-urgent' : ''}">
      <span class="turn-clock-label">Thời gian</span>
      <strong class="turn-clock-value">${remainingSeconds}s</strong>
      <span class="turn-clock-track"><span class="turn-clock-fill" style="width:${pct}%"></span></span>
    </div>
  `;
}

function renderStatusText(room: RoomSnapshot['room'], currentPlayer?: PlayerView): string {
  if (room.status === 'WAITING')  return `⏳ Chờ người chơi (${room.players.length}/${room.maxPlayers})`;
  if (room.status === 'FINISHED') return `✅ Ván ${room.roundNumber} kết thúc`;
  return currentPlayer ? `🎴 Lượt của ${esc(currentPlayer.name)}` : `▶ Ván ${room.roundNumber}`;
}

function renderCenterPrompt(room: RoomSnapshot['room']): string {
  if (room.status !== 'PLAYING') return 'Đang chờ bắt đầu ván mới…';
  if (room.openingRequiredCardId) {
    return `Ván đầu tiên — bộ mở phải có ${esc(cardTextFromId(room.openingRequiredCardId))}`;
  }
  return 'Vòng mới — người mở có thể đánh bất kỳ bộ hợp lệ';
}

function getRoomBetAmount(room: { betAmount?: number }): number {
  return clampBetAmount(room.betAmount ?? DEFAULT_BET_AMOUNT);
}

function getSelectedSuggestion(me: RoomSnapshot['me']): SuggestedPlayView | null {
  if (me?.suggestedPlays?.[0]) return me.suggestedPlays[0];
  if (!state.snapshot || !me?.isTurn) return null;
  return getClientSuggestedPlays(state.snapshot.room, me)[0] || null;
}

function getClientSuggestedPlays(room: RoomSnapshot['room'], me: NonNullable<RoomSnapshot['me']>): SuggestedPlayView[] {
  if (room.status !== 'PLAYING' || !me.isTurn || !me.hand.length) return [];

  const openingRequiredCardId =
    room.firstGame && room.roundNumber === 1 && !room.lastPlay ? room.openingRequiredCardId : undefined;
  const targetCombo = room.lastPlay ? normalizeCombo(room.lastPlay.combo) : null;

  const candidates: ComboView[] = [];
  const sortedHand = sortCards(me.hand);
  const totalMasks = 1 << sortedHand.length;
  for (let mask = 1; mask < totalMasks; mask += 1) {
    const cards = sortedHand.filter((_, index) => (mask & (1 << index)) !== 0);
    if (openingRequiredCardId && !cards.some(card => card.id === openingRequiredCardId)) continue;

    const combo = classifyCards(cards);
    if (!combo) continue;
    if (targetCombo && !canBeatCombo(combo, targetCombo)) continue;
    candidates.push(combo);
  }

  const seen = new Set<string>();
  return candidates
    .sort((left, right) => suggestionScore(left) - suggestionScore(right))
    .filter(combo => {
      const key = combo.cards.map(card => card.id).join(',');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 12)
    .map(combo => ({
      combo,
      cardIds: combo.cards.map(card => card.id),
    }));
}

function classifyCards(cards: Card[]): ComboView | null {
  if (cards.length === 0) return null;
  const sorted = sortCards(cards);
  const ranks = sorted.map(card => card.rank);
  const rankSet = new Set(ranks);
  const highCard = sorted[sorted.length - 1];
  const base: ComboView = {
    type: 'single',
    cards: sorted,
    highRankValue: rankValue(highCard.rank),
    highSuitValue: suitValue(highCard.suit),
    length: sorted.length,
  };

  if (sorted.length === 1) return { ...base, type: 'single' };
  if (rankSet.size === 1) {
    if (sorted.length === 2) return { ...base, type: 'pair' };
    if (sorted.length === 3) return { ...base, type: 'triple' };
    if (sorted.length === 4) return { ...base, type: 'fourKind' };
  }

  return classifyStraight(sorted) || classifyDoubleSequence(sorted);
}

function classifyStraight(cards: Card[]): ComboView | null {
  if (cards.length < 3 || cards.some(card => card.rank === '2')) return null;
  const values = cards.map(card => rankValue(card.rank));
  if (new Set(values).size !== values.length) return null;

  const sortedValues = values.slice().sort((a, b) => a - b);
  for (let index = 1; index < sortedValues.length; index += 1) {
    if (sortedValues[index] !== sortedValues[index - 1] + 1) return null;
  }

  const highRankValue = sortedValues[sortedValues.length - 1];
  const highCards = cards.filter(card => rankValue(card.rank) === highRankValue);
  return {
    type: 'straight',
    cards: sortCards(cards),
    highRankValue,
    highSuitValue: Math.max(...highCards.map(card => suitValue(card.suit))),
    length: cards.length,
  };
}

function classifyDoubleSequence(cards: Card[]): ComboView | null {
  if (cards.length < 6 || cards.length % 2 !== 0 || cards.some(card => card.rank === '2')) return null;

  const groups = groupByRank(cards);
  if (Array.from(groups.values()).some(group => group.length !== 2)) return null;

  const values = Array.from(groups.keys()).map(rankValue).sort((a, b) => a - b);
  for (let index = 1; index < values.length; index += 1) {
    if (values[index] !== values[index - 1] + 1) return null;
  }

  const highRankValue = values[values.length - 1];
  const highRank = RANKS[highRankValue];
  const highCards = groups.get(highRank) || [];
  return {
    type: 'doubleSequence',
    cards: sortCards(cards),
    highRankValue,
    highSuitValue: Math.max(...highCards.map(card => suitValue(card.suit))),
    length: cards.length,
    pairCount: cards.length / 2,
  };
}

function normalizeCombo(combo: ComboView): ComboView | null {
  if (
    combo.highRankValue !== undefined &&
    combo.highSuitValue !== undefined &&
    combo.length !== undefined
  ) {
    return combo;
  }
  return classifyCards(combo.cards);
}

function canBeatCombo(combo: ComboView, target: ComboView): boolean {
  const comboLength = combo.length ?? combo.cards.length;
  const targetLength = target.length ?? target.cards.length;

  if (combo.type === target.type && comboLength === targetLength) {
    return compareSameCombo(combo, target) > 0;
  }

  if (
    combo.type === 'doubleSequence' &&
    target.type === 'doubleSequence' &&
    (combo.pairCount || 0) > (target.pairCount || 0)
  ) {
    return true;
  }

  if (target.type === 'single' && target.cards[0]?.rank === '2') {
    return (combo.type === 'doubleSequence' && (combo.pairCount || 0) >= 3) || combo.type === 'fourKind';
  }

  if (target.type === 'pair' && target.cards[0]?.rank === '2') {
    return combo.type === 'doubleSequence' && (combo.pairCount || 0) >= 4;
  }

  if (target.type === 'doubleSequence' && (target.pairCount || 0) === 3) {
    return combo.type === 'fourKind' || (combo.type === 'doubleSequence' && (combo.pairCount || 0) >= 4);
  }

  if (target.type === 'fourKind') {
    return combo.type === 'doubleSequence' && (combo.pairCount || 0) >= 4;
  }

  return false;
}

function compareSameCombo(left: ComboView, right: ComboView): number {
  const leftRank = left.highRankValue ?? rankValue(sortCards(left.cards).at(-1)?.rank || '3');
  const rightRank = right.highRankValue ?? rankValue(sortCards(right.cards).at(-1)?.rank || '3');
  if (leftRank !== rightRank) return leftRank - rightRank;

  const leftSuit = left.highSuitValue ?? suitValue(sortCards(left.cards).at(-1)?.suit || 'S');
  const rightSuit = right.highSuitValue ?? suitValue(sortCards(right.cards).at(-1)?.suit || 'S');
  return leftSuit - rightSuit;
}

function suggestionScore(combo: ComboView): number {
  const typeOrder: Record<string, number> = {
    single: 0,
    pair: 1,
    triple: 2,
    straight: 3,
    doubleSequence: 4,
    fourKind: 5,
  };
  return (combo.length ?? combo.cards.length) * 1000 +
    (combo.highRankValue ?? 0) * 20 +
    (combo.highSuitValue ?? 0) +
    (typeOrder[combo.type] || 0) * 100;
}

function sortCards(cards: Card[]): Card[] {
  return cards.slice().sort((a, b) => rankValue(a.rank) - rankValue(b.rank) || suitValue(a.suit) - suitValue(b.suit));
}

function groupByRank(cards: Card[]): Map<Rank, Card[]> {
  const groups = new Map<Rank, Card[]>();
  for (const card of cards) {
    const current = groups.get(card.rank) || [];
    current.push(card);
    groups.set(card.rank, current);
  }
  return groups;
}

function rankValue(rank: Rank): number {
  return RANKS.indexOf(rank);
}

function suitValue(suit: Suit): number {
  return SUITS.indexOf(suit);
}

function getServerNow(): number {
  return Date.now() + state.serverClockOffsetMs;
}

function getTurnRemainingMs(room: RoomSnapshot['room']): number {
  if (!room.turnDeadlineAt) return 0;
  return Math.max(0, room.turnDeadlineAt - getServerNow());
}

function getTurnRemainingSeconds(room: RoomSnapshot['room']): number {
  return Math.ceil(getTurnRemainingMs(room) / 1000);
}

function getTurnRemainingPct(room: RoomSnapshot['room']): number {
  if (!room.turnStartedAt || !room.turnDeadlineAt || room.turnDeadlineAt <= room.turnStartedAt) return 0;
  const total = room.turnDeadlineAt - room.turnStartedAt;
  return Math.max(0, Math.min(100, (getTurnRemainingMs(room) / total) * 100));
}

function updateTurnCountdown(): void {
  const room = state.snapshot?.room;
  if (!room || room.status !== 'PLAYING') return;

  const remainingSeconds = getTurnRemainingSeconds(room);
  const pct = getTurnRemainingPct(room);
  const clock = q<HTMLElement>('.turn-clock');
  const value = q<HTMLElement>('.turn-clock-value');
  const fill = q<HTMLElement>('.turn-clock-fill');

  if (value) value.textContent = `${remainingSeconds}s`;
  if (fill) fill.style.width = `${pct}%`;
  if (clock) clock.classList.toggle('is-urgent', remainingSeconds <= 5);
}

function updateBetAmountDisplay(amount: number): void {
  const hint = q<HTMLElement>('#betAmountHint');
  if (hint) hint.textContent = fmtMoney(amount);
}

function normalizedPlayerName(): string {
  const v = state.playerName.trim() || `Khách ${socket.id?.slice(0, 4) || ''}`;
  state.playerName = v;
  localStorage.setItem('tienlen_player_name', v);
  return v;
}

function esc(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
function escAttr(v: string): string { return esc(v); }

/* ══════════════════════════════════════════
   BIND EVENTS
══════════════════════════════════════════ */
function bindEvents(): void {
  // ── Back button — smart navigation ──
  q('#backBtn')?.addEventListener('click', (e) => {
    e.preventDefault();
    navigateToDashboard();
  });

  // ── Login button (lobby, no session) ──
  q('#loginBtn')?.addEventListener('click', async () => {
    const player = await ensureArcadePlayer(state.playerName || undefined);
    if (player) {
      state.arcadePlayer = player;
      state.playerName = player.name;
      state.balance = await loadBankAccount(player);
      localStorage.setItem('tienlen_player_name', player.name);
      render();
    }
  });

  // ── Player name input (only when no session) ──
  q<HTMLInputElement>('#playerName')?.addEventListener('input', e => {
    if (state.arcadePlayer) return; // locked when session exists
    state.playerName = (e.target as HTMLInputElement).value;
    localStorage.setItem('tienlen_player_name', state.playerName);
  });

  q<HTMLInputElement>('#roomName')?.addEventListener('input', e => {
    state.roomName = (e.target as HTMLInputElement).value;
  });

  const betAmountInput = q<HTMLInputElement>('#betAmount');
  betAmountInput?.addEventListener('input', e => {
    const input = e.target as HTMLInputElement;
    state.betAmount = clampBetAmount(Number(input.value));
    localStorage.setItem('tienlen_bet_amount', String(state.betAmount));
    updateBetAmountDisplay(state.betAmount);
  });
  betAmountInput?.addEventListener('blur', e => {
    const input = e.target as HTMLInputElement;
    state.betAmount = clampBetAmount(Number(input.value));
    input.value = String(state.betAmount);
    localStorage.setItem('tienlen_bet_amount', String(state.betAmount));
    updateBetAmountDisplay(state.betAmount);
  });

  q('#createRoom')?.addEventListener('click', () => {
    const playerName = normalizedPlayerName();
    const betAmount = clampBetAmount(state.betAmount);
    if (state.arcadePlayer && state.balance < betAmount) {
      state.error = `Bank account cần ít nhất ${fmtMoney(betAmount)} để tạo bàn này.`;
      render();
      return;
    }
    socket.emit('tienlen-create-room', {
      playerName,
      roomName: state.roomName || `${playerName} mở bàn`,
      maxPlayers: 4,
      betAmount,
    });
  });

  q('#refreshRooms')?.addEventListener('click', () => {
    socket.emit('tienlen-get-rooms');
  });

  qAll<HTMLButtonElement>('.join-room').forEach(btn => {
    btn.addEventListener('click', () => {
      const room = state.rooms.find(item => item.id === btn.dataset.roomId);
      if (room && state.arcadePlayer && state.balance < getRoomBetAmount(room)) {
        state.error = `Bank account cần ít nhất ${fmtMoney(getRoomBetAmount(room))} để vào bàn này.`;
        render();
        return;
      }
      socket.emit('tienlen-join-room', {
        roomId: btn.dataset.roomId,
        playerName: normalizedPlayerName(),
      });
    });
  });

  q('#leaveRoom')?.addEventListener('click', () => {
    if (!state.snapshot) return;
    socket.emit('tienlen-leave-room', { roomId: state.snapshot.room.id });
  });

  qAll<HTMLButtonElement>('.start-game').forEach(btn => {
    btn.addEventListener('click', () => {
      if (!state.snapshot) return;
      if (state.arcadePlayer && state.balance < getRoomBetAmount(state.snapshot.room)) {
        state.error = `Bank account cần ít nhất ${fmtMoney(getRoomBetAmount(state.snapshot.room))} để bắt đầu ván.`;
        render();
        return;
      }
      state.selected.clear();
      socket.emit('tienlen-start-game', { roomId: state.snapshot.room.id });
    });
  });

  q('#playCards')?.addEventListener('click', () => {
    if (!state.snapshot || state.selected.size === 0) return;
    socket.emit('tienlen-play', {
      roomId: state.snapshot.room.id,
      cardIds: Array.from(state.selected),
    });
  });

  q('#passTurn')?.addEventListener('click', () => {
    if (!state.snapshot) return;
    state.selected.clear();
    socket.emit('tienlen-pass', { roomId: state.snapshot.room.id });
  });

  q('#hintPlay')?.addEventListener('click', () => {
    const suggestion = getSelectedSuggestion(state.snapshot?.me);
    if (!state.snapshot || !suggestion) return;

    state.selected = new Set(suggestion.cardIds);
    render();
  });

  // ── Card selection — DOM-only toggle, no full re-render ──
  qAll<HTMLButtonElement>('.hand-wrap .card[data-card-id]').forEach((btn, idx) => {
    btn.addEventListener('click', () => {
      const cardId = btn.dataset.cardId;
      if (!cardId || !state.snapshot?.me?.isTurn) return;

      if (state.selected.has(cardId)) {
        state.selected.delete(cardId);
        btn.classList.remove('is-selected');
        btn.style.zIndex = String(idx); // restore original fan z-index
      } else {
        state.selected.add(cardId);
        btn.classList.add('is-selected');
        btn.style.zIndex = '15';
      }

      updateActionBar();
    });
  });
}

function q<T extends Element>(sel: string): T | null {
  return document.querySelector<T>(sel);
}
function qAll<T extends Element>(sel: string): NodeListOf<T> {
  return document.querySelectorAll<T>(sel);
}

/* ══════════════════════════════════════════
   INIT
══════════════════════════════════════════ */
render(); // Initial paint (shows connecting state)
initSession(); // Async: loads/prompts session then re-renders
window.setInterval(updateTurnCountdown, 250);
